/**
 * โหลดข้อความสถานะตาม ROB_ID จากฐาน (API)
 * แยกตาม REGISTER_STATUS ใน content.byStatus
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { mua } from "./api";
import { MESSAGES, PORTFOLIO_MAJOR_CODES, isRobIn } from "./admissionRound";

export const EMPTY_STATUS_SLOT = {
  titleText: "",
  bodyText: "",
  noteVisible: false,
  noteText: "",
  announceVisible: false,
  announceUrl: "",
};

export const DEFAULT_ROUND_CONTENT = {
  robId: "_default",
  noteVisible: true,
  noteText: MESSAGES.noteStudyInterviewAnnounce,
  announceVisible: true,
  announceUrl: "",
  messages: { ...MESSAGES },
  flags: {
    showInterviewRoom: false,
    statusInterviewEligible: false,
    statusPortfolioRound: false,
    commentInterviewNote: false,
    disableEditVut: false,
    /** รหัสสาขาที่ต้องสัมภาษณ์ (เมื่อเปิดแยกสาขา) — ว่าง = ใช้ PORTFOLIO_MAJOR_CODES */
    interviewMajorIds: [],
    /** ข้อความสำหรับสาขาที่ไม่ต้องสัมภาษณ์ */
    noInterviewText: "",
  },
  byStatus: {},
};

function mergeRoundContent(raw, robId) {
  const base = DEFAULT_ROUND_CONTENT;
  const data = raw && typeof raw === "object" ? raw : {};
  const mergedFlags = { ...base.flags, ...(data.flags || {}) };
  mergedFlags.interviewMajorIds = normalizeMajorIds(mergedFlags.interviewMajorIds);
  mergedFlags.noInterviewText = String(mergedFlags.noInterviewText || "");
  return {
    ...base,
    ...data,
    robId: String(robId || data.robId || base.robId),
    messages: { ...base.messages, ...(data.messages || {}) },
    flags: mergedFlags,
    byStatus: { ...(data.byStatus || {}) },
  };
}

/** แปลง input (array / string คั่น comma หรือขึ้นบรรทัด) เป็น array รหัสสาขา */
export function normalizeMajorIds(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[\s,;|]+/)
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * รายการสาขาที่ต้องสัมภาษณ์ของรอบนี้
 * - ถ้า flags.interviewMajorIds มีค่า → ใช้ค่านั้น
 * - ไม่งั้น fallback PORTFOLIO_MAJOR_CODES (logic เดิม)
 */
export function resolveInterviewMajorIds(content) {
  const fromFlags = normalizeMajorIds(content?.flags?.interviewMajorIds);
  if (fromFlags.length > 0) {
    return fromFlags;
  }
  return PORTFOLIO_MAJOR_CODES.map(String);
}

export function isMajorInInterviewList(content, majorId) {
  if (majorId == null || String(majorId).trim() === "") {
    return false;
  }
  return resolveInterviewMajorIds(content).includes(String(majorId));
}

/**
 * รอบนี้แยกสาขา (บางสาขาสัมภาษณ์ / บางสาขาไม่ต้อง)
 * = flags.statusPortfolioRound หรือ fallback ROB_GROUPS
 */
export function isMixedInterviewRound(content, robId) {
  return resolveRoundFlag(content, "statusPortfolioRound", robId);
}

/**
 * สาขานี้ต้องเข้าสัมภาษณ์หรือไม่ (ในรอบนี้)
 * - โหมดแยกสาขา: เฉพาะสาขาใน interviewMajorIds
 * - โหมดรอบสัมภาษณ์ทั่วไป: ทุกสาขา
 * - นอกนั้น: ไม่ต้อง
 */
export function majorNeedsInterview(content, robId, majorId) {
  if (isMixedInterviewRound(content, robId)) {
    return isMajorInInterviewList(content, majorId);
  }
  if (resolveRoundFlag(content, "statusInterviewEligible", robId)) {
    return true;
  }
  return false;
}

/** ข้อความสำหรับสาขาที่ไม่ต้องสัมภาษณ์ */
export function resolveNoInterviewText(content, statusContent) {
  const fromFlags = String(content?.flags?.noInterviewText || "").trim();
  if (fromFlags) {
    return fromFlags;
  }
  const fromMessages = String(content?.messages?.noInterviewAnnounce || MESSAGES.noInterviewAnnounce || "").trim();
  if (fromMessages) {
    return fromMessages;
  }
  return String(statusContent?.bodyText || statusContent?.titleText || "").trim();
}

const cache = new Map();

export async function fetchRoundContent(robId) {
  const id = String(robId || "").trim();
  if (!id) {
    return mergeRoundContent(null, "_default");
  }
  if (cache.has(id)) {
    return cache.get(id);
  }
  try {
    const res = await axios.get(`${mua}/admissionnew2022/getRoundContent/${encodeURIComponent(id)}`);
    const payload = res.data?.data ?? res.data;
    const merged = mergeRoundContent(payload, id);
    cache.set(id, merged);
    return merged;
  } catch (error) {
    console.warn("fetchRoundContent failed", id, error);
    const fallback = mergeRoundContent(null, id);
    cache.set(id, fallback);
    return fallback;
  }
}

export function clearRoundContentCache(robId) {
  if (robId) {
    cache.delete(String(robId));
  } else {
    cache.clear();
  }
}

export function resolveRoundFlag(content, flagKey, robId) {
  const flags = content?.flags;
  if (flags && Object.prototype.hasOwnProperty.call(flags, flagKey) && flags[flagKey] !== null) {
    // array / string flags ไม่ใช่ boolean
    if (flagKey === "interviewMajorIds" || flagKey === "noInterviewText") {
      return flags[flagKey];
    }
    return Boolean(flags[flagKey]);
  }
  return isRobIn(flagKey, robId);
}

/** ดึงช่องข้อความของสถานะหนึ่ง — fallback messages เก่าถ้ายังไม่มี byStatus */
export function resolveStatusContent(content, registerStatus) {
  const sid = String(registerStatus ?? "");
  const slot = content?.byStatus?.[sid];
  if (slot && typeof slot === "object") {
    return { ...EMPTY_STATUS_SLOT, ...slot };
  }

  // fallback จากโครง messages เดิม (เมื่อยังไม่มี byStatus ในฐาน)
  const m = content?.messages || MESSAGES;
  const fallbacks = {
    "0": { titleText: "อยู่ระหว่างการชำระค่าสมัคร" },
    "1": {
      titleText: m.interviewEligible || "มีสิทธิ์เข้ารับการสอบสัมภาษณ์",
      bodyText: m.waitAnnouncePass || "",
      noteVisible: content?.noteVisible !== false,
      noteText: content?.noteText || m.noteStudyInterviewAnnounce || "",
      announceVisible: content?.announceVisible !== false,
      announceUrl: content?.announceUrl || "",
    },
    "2": { titleText: "เข้ารับการสอบสัมภาษณ์" },
    "3": { titleText: "ไม่ผ่านการคัดเลือก" },
    "4": {
      titleText: "ผู้มีสิทธิ์เข้าศึกษา",
      bodyText: m.admitPayWindowMay11_15 || "",
      noteVisible: true,
      noteText: "หมายเหตุ*",
      announceVisible: content?.announceVisible !== false,
      announceUrl: content?.announceUrl || "",
    },
    "5": { titleText: "สละสิทธิ์เข้าศึกษา" },
    "6": {
      titleText: "ยืนยันสิทธิ์",
      bodyText: m.reportAfterPay || "",
    },
    "9": { titleText: "ยืนยันสิทธิ์" },
    "11": {
      titleText: "ไม่ผ่านการคัดเลือก",
      bodyText: m.nextRoundOpenHint || "",
    },
    "12": {
      titleText: "ไม่ผ่านเกณฑ์การสมัคร",
      bodyText: "ติดต่องานรับสมัครนักศึกษา โทร 055-706-547",
    },
    "41": {
      titleText: "ผ่านการคัดเลือกเป็น",
      bodyText:
        "1. ผู้ที่ได้รับการคัดเลือกเป็นตัวสำรอง มหาวิทยาลัยฯ จะประกาศผลการคัดเลือก เข้าศึกษาต่อเพิ่มเติมสำหรับทะเบียนสำรอง (ตัวสำรอง) ในวันที่ 30 เมษายน 2569",
      noteVisible: true,
      noteText: "หมายเหตุ*",
    },
  };
  return { ...EMPTY_STATUS_SLOT, ...(fallbacks[sid] || {}) };
}

export function useRoundContent(robId) {
  const [content, setContent] = useState(() => mergeRoundContent(null, robId || "_default"));
  const [loading, setLoading] = useState(Boolean(robId));

  useEffect(() => {
    let cancelled = false;
    if (!robId) {
      setContent(mergeRoundContent(null, "_default"));
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    void fetchRoundContent(robId).then((data) => {
      if (!cancelled) {
        setContent(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [robId]);

  return { content, loading };
}

export function useStatusContent(robId, registerStatus) {
  const { content, loading } = useRoundContent(robId);
  const statusContent = useMemo(
    () => resolveStatusContent(content, registerStatus),
    [content, registerStatus]
  );
  return { content, statusContent, loading };
}
